import { Component, type ErrorInfo, type ReactNode } from "react";
import { Icon } from "../ui/Icon";
import { Button } from "../ui/primitives";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Names what failed in the message, e.g. "The assistant". */
  label?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Contains a rendering failure to the part of the page that failed.
 *
 * Without a boundary, one unexpected value from the API unmounts the whole
 * application and leaves a blank window. With one, the navigation, top bar and
 * assistant keep working, the user is told what happened, and they can retry or
 * go elsewhere. The shell keys the route boundary by path, so navigating away
 * always starts clean.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept for whoever is diagnosing; the screen itself shows a plain summary.
    console.error("A screen failed to render", error, info.componentStack);
  }

  private readonly retry = () => this.setState({ error: null });

  override render() {
    const { error } = this.state;

    if (!error) return this.props.children;

    return (
      <div className="state" role="alert" style={{ minHeight: 420 }}>
        <Icon.alert />
        <b>
          {this.props.label
            ? `${this.props.label} could not be shown`
            : "This screen could not be shown"}
        </b>
        <p>
          Something in the information it received was not in the form it expected. Nothing
          was changed. Try again, or go back and carry on elsewhere.
        </p>
        {import.meta.env.DEV && (
          <p className="mono muted" style={{ font: "var(--t-small)", overflowWrap: "anywhere" }}>
            {error.message}
          </p>
        )}
        <div className="row" style={{ gap: 8, justifyContent: "center" }}>
          <Button tone="primary" onClick={this.retry}>
            Try again
          </Button>
          <Button onClick={() => window.history.back()}>Go back</Button>
        </div>
      </div>
    );
  }
}
